import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { EyeIcon, EyeOffIcon, LockIcon, MailIcon } from 'lucide-react';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
  const {
    login,
    forgotPassword
  } = useAuth();
  const navigate = useNavigate();
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const success = await login(username, password);
      if (success) {
        navigate('/');
      } else {
        setError('Invalid username or password');
      }
    } catch (err) {
      setError('An error occurred during login');
    } finally {
      setIsLoading(false);
    }
  };
  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const success = await forgotPassword(forgotPasswordEmail);
      if (success) {
        setForgotPasswordSuccess(true);
      } else {
        setError('Failed to process your request');
      }
    } catch (err) {
      setError('An error occurred');
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4 bg-gradient-to-br from-gray-50 to-gray-100" data-id="element-587">
      <div className="bg-white rounded-xl shadow-xl overflow-hidden max-w-md w-full" data-id="element-588">
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-6" data-id="element-589">
          <h1 className="text-white text-2xl font-bold text-center" data-id="element-590">
            Enterprise Portal
          </h1>
        </div>
        {!forgotPasswordMode ? <div className="p-8" data-id="element-591">
            <h2 className="text-xl font-semibold text-gray-800 mb-6 text-center" data-id="element-592">
              Sign In
            </h2>
            {error && <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-600 rounded-lg text-sm" data-id="element-593">
                {error}
              </div>}
            <form onSubmit={handleLogin} className="space-y-6" data-id="element-594">
              <Input id="username" type="text" value={username} onChange={e => setUsername(e.target.value)} label="Username / Email" icon={<MailIcon className="h-5 w-5" data-id="element-596" />} placeholder="Enter your username or email" required data-id="element-595" />
              <div data-id="element-597">
                <Input id="password" type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} label="Password" icon={<LockIcon className="h-5 w-5" data-id="element-599" />} placeholder="Enter your password" required data-id="element-598" />
                <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center top-6" onClick={() => setShowPassword(!showPassword)} data-id="element-600">
                  {showPassword ? <EyeOffIcon className="h-5 w-5 text-gray-400" data-id="element-601" /> : <EyeIcon className="h-5 w-5 text-gray-400" data-id="element-602" />}
                </button>
              </div>
              <div className="flex items-center justify-between" data-id="element-603">
                <div className="flex items-center" data-id="element-604">
                  <input id="remember-me" type="checkbox" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-605" />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700" data-id="element-606">
                    Remember me
                  </label>
                </div>
                <button type="button" className="text-sm font-medium text-blue-600 hover:text-blue-500" onClick={() => setForgotPasswordMode(true)} data-id="element-607">
                  Forgot password?
                </button>
              </div>
              <Button type="submit" variant="primary" size="lg" isLoading={isLoading} fullWidth data-id="element-608">
                Sign in
              </Button>
            </form>
            <div className="mt-6" data-id="element-609">
              <p className="text-xs text-gray-500 text-center" data-id="element-610">
                Demo credentials: adminmaker / Test@1234 or adminchecker /
                Test@1234
              </p>
            </div>
          </div> : <div className="p-8" data-id="element-611">
            <h2 className="text-xl font-semibold text-gray-800 mb-6 text-center" data-id="element-612">
              Forgot Password
            </h2>
            {error && <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-600 rounded-lg text-sm" data-id="element-613">
                {error}
              </div>}
            {forgotPasswordSuccess ? <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-600 rounded-lg text-sm" data-id="element-614">
                Password reset instructions have been sent to your email.
              </div> : <form onSubmit={handleForgotPassword} className="space-y-6" data-id="element-615">
                <Input id="email" type="email" value={forgotPasswordEmail} onChange={e => setForgotPasswordEmail(e.target.value)} label="Email" icon={<MailIcon className="h-5 w-5" data-id="element-617" />} placeholder="Enter your email" required data-id="element-616" />
                <Button type="submit" variant="primary" size="lg" isLoading={isLoading} fullWidth data-id="element-618">
                  Reset Password
                </Button>
                <Button type="button" variant="outline" size="lg" fullWidth onClick={() => setForgotPasswordMode(false)} data-id="element-619">
                  Back to Login
                </Button>
              </form>}
          </div>}
      </div>
    </div>;
};
export default Login;